/**
 * The shared `@tanstack/query` `QueryClient` — frontend.md §2.2, §4.2.
 *
 * Server-cache state is `@tanstack/query`'s job: every REST read goes through it (caching,
 * dedup, background refetch, the Health/Sources polling). This module configures the one
 * client `<AppRoot>` provides.
 *
 * KEY CHOICES:
 *  - **`retry: false`.** The backend owns retry with exponential backoff (conventions.md
 *    §7 / frontend.md §4.2 — "the frontend does not itself retry external calls"). A
 *    failed query surfaces to the user, who re-issues on an explicit action. Letting
 *    `@tanstack/query` retry would double-retry external calls and hammer the backend.
 *  - **`refetchOnWindowFocus: false`.** The app is a Tauri webview, not a browser tab the
 *    user alt-tabs between; focus-refetch would be noise. The surfaces that need fresh
 *    data (Health, Sources) opt into explicit polling via `refetchInterval`.
 *  - **`staleTime` default of 60s.** Most corpus reads are not changing second-to-second;
 *    a 60s stale window means re-navigating to a surface within a minute serves the cache
 *    instantly (no spinner / round-trip). Polling surfaces override it.
 *  - **`gcTime` of 30min.** Keep fetched data resident across in-session navigation so
 *    revisiting the editor / search / a note is instant from cache rather than a cold refetch
 *    (G6 perf — 2026-05-22). Memory cost is small for the corpus sizes in play.
 *  - **`refetchOnMount: false`** plus the stale window — mounting a surface shows cached data
 *    immediately and only revalidates in the background once the stale window passes.
 */

import { QueryClient } from '@tanstack/react-query';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // The backend owns retry (conventions.md §7) — the client never retries.
        retry: false,
        refetchOnWindowFocus: false,
        // Show cached data instantly on remount; revalidate in the background when stale.
        refetchOnMount: false,
        staleTime: 60_000,
        gcTime: 30 * 60_000,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * The query-key factory — a single namespace so invalidation is precise and typo-safe.
 * Every hook builds its key from here; a mutation invalidates by the matching prefix.
 */
export const queryKeys = {
  search: (params: unknown) => ['search', params] as const,
  note: (id: string) => ['note', id] as const,
  noteChunks: (id: string) => ['note-chunks', id] as const,
  entities: (params: unknown) => ['entities', params] as const,
  entity: (id: string) => ['entity', id] as const,
  entityTimeline: (id: string, granularity: string) =>
    ['entity-timeline', id, granularity] as const,
  entityCooccurrences: (id: string) => ['entity-cooccurrences', id] as const,
  tags: (params: unknown) => ['tags', params] as const,
  relatedTags: (tag: string) => ['related-tags', tag] as const,
  tagTimeline: (tag: string, granularity: string) => ['tag-timeline', tag, granularity] as const,
  neighborhood: (params: unknown) => ['neighborhood', params] as const,
  actionItems: (params: unknown) => ['action-items', params] as const,
  contradictions: (params: unknown) => ['contradictions', params] as const,
  // frontend-supporting endpoints
  systemStatus: () => ['system-status'] as const,
  costTelemetry: () => ['cost-telemetry'] as const,
  extractionQueue: () => ['health', 'extraction-queue'] as const,
  inferenceMetrics: () => ['health', 'inference-metrics'] as const,
  failedExtractions: (page: number) => ['health', 'failed-extractions', page] as const,
  softDeleted: (page: number) => ['health', 'soft-deleted', page] as const,
  conflictedNotes: (page: number) => ['health', 'conflicted-notes', page] as const,
  countReport: (report: string) => ['health', 'count-report', report] as const,
  joplinStatus: () => ['sources', 'joplin-status'] as const,
  settings: () => ['settings'] as const,
  savedSearches: () => ['saved-searches'] as const,
} as const;
